import { makeEnum, makeEnumWithData, makeOptions,
  makeEnumWithDataAndLabels,
  setupClass, lookupData, Matches,
  interpolateInMap, doubleInterpolateInMap,
  IdsMap, PleaseContactStr,
  IntervalTimer,
} from '../Base.js'
import { InputComponent } from '../Common/InputComponent.js'
import { CalcContext } from '../Common/CalcContext.js'
import { arraysEqual } from '../SharedUtils.js'
import * as ser from '../Common/SerUtil.js'
import { ComponentArray } from '../Common/ComponentArray.js'

import { Field, FieldType, FieldGroup, SelectOrManualInput, } from '../Common/Field.js'
import { FieldWithVariableUnits, MultiTieredSelect, } from '../Common/FieldUtils.js'
import { Units } from '../Common/Units.js'

import {
  GetLightingPowerTable,
  GetSpecialAllowanceFactorsTable,
} from '../Data/LightingPower.js'
import { LuminaireType, GetLuminaireDataTable } from '../Data/LuminaireData.js'

import { gApp, DebugOn } from '../Globals.js'

import * as officeloads from '../MaterialData/OfficeLoadFactors.js'
import { ApplianceDataTable } from '../MaterialData/ApplianceDataTable.js'
import { SpaceTypesDataTable } from '../MaterialData/SpaceTypesDataTable.js'
import { ScheduleInput } from './ScheduleInput.js'
import { TypesSet } from '../Components/TypesSet.js'

export let OccupancyEntryType = makeEnum({
  ByPeople: 'Enter # of people',
  BySpaceDensity: 'Enter by density',
})

/*
Activity Level
Seated	
Seated, very light work	
Moderately active office work	
Standing, light work; walking	
Walking, standing	
Sedentary work	
Light bench work	
Moderate dancing	
Walking 3 mph; light machine work	
Bowling	
Heavy work	
Heavy machine work; lifting	
Athletics	
*/
// TODO - this enum should really be read from the table
let ActivityLevel = makeEnum({
  Seated: 'Seated',
  SeatedVeryLightWork: 'Seated, very light work',
  ModeratelyActiveOfficeWork: 'Moderately active office work',
  StandingLightWorkWalking: 'Standing, light work; walking',
  WalkingStanding: 'Walking, standing',
  SedentaryWork: 'Sedentary work',
  LightBenchWork: 'Light bench work',
  ModerateDancing: 'Moderate dancing',
  Walking3mphLightMachineWork: 'Walking 3 mph; light machine work',
  Bowling: 'Bowling',
  HeavyWork: 'Heavy work',
  HeavyMachineWorkLifting: 'Heavy machine work; lifting',
  Athletics: 'Athletics',
})

class PeopleInternals extends InputComponent {
  init(spaceInternals) {
    this.spaceInternals = spaceInternals;

    this.occupancyEntryType = Field.makeSelect("Occupancy input", OccupancyEntryType, {bold: true})
    this.occupancyCount = new Field({
      name: 'Number of occupants',
      type: FieldType.People,
      defaultValue: 0,
    })
    this.occupancyCount.setVisibility(() => {
      return this.occupancyEntryType.value == OccupancyEntryType.ByPeople
    })
    this.occupancyDensity = new Field({
      name: 'Occupancy density',
      type: FieldType.PeoplePer1000ft2,
      defaultValue: 0,
    })
    this.occupancyDensity.setVisibility(() => {
      return this.occupancyEntryType.value == OccupancyEntryType.BySpaceDensity
    })
    this.helpInfo = null;
    this.updater.addWatchEffect('occupancy-entry-type', () => {
      let res = this.getNumOccupants();
      this.helpInfo = res.helpInfo;
    })
    this.occupancyTableData = SpaceTypesDataTable.getInstance().getOccupancyTableData()

    this.activityLevel = Field.makeSelect('Activity Level', ActivityLevel);
    this.activityLevelHelpInfo = null;
    this.updater.addWatchEffect('activity-level', () => {
      let data = this.getActivityLevelData(CalcContext.create());
      this.activityLevelHelpInfo = {
        helpText: `The activity level determines the sensible and latent loads per person (as well as the radiant fraction).`,
        relatedValues: {
          'Sensible Load': {
            label: 'Sensible load per person',
            value: data.S_P,
            units: Units.Load,
          },
          'Radiant Fraction': {
            label: 'Radiant fraction',
            value: data.F_rad * 100.0,
            units: Units.Percent,
          },
          'Latent Load': {
            label: 'Latent load per person',
            value: data.Latent_Heat,
            units: Units.Load,
          },
        }
      }
    })

    this.scheduleInput = ScheduleInput.create({
      name: 'Occupancy Schedule',
    })

    // Outputs
    this.outputSensibleLoad = new Field({
      name: 'Sensible load',
      type: FieldType.Load,
      isOutput: true,
    })
    this.outputRadiantFraction = new Field({
      name: 'Radiant fraction',
      type: FieldType.Percent,
      isOutput: true,
    })
    this.outputLatentLoad = new Field({
      name: 'Latent load',
      type: FieldType.Load,
      isOutput: true,
    })
    this.updater.addWatchEffect('outputs', () => {
      let ctx = CalcContext.create();
      let data = this.getActivityLevelData(ctx);
      let numOccupants = this.getNumOccupants().result;
      this.outputSensibleLoad.value = data.S_P * numOccupants;
      this.outputRadiantFraction.value = data.F_rad * 100;
      this.outputLatentLoad.value = data.Latent_Heat * numOccupants;
    })

    this.miscFields = [
      'activityLevel',
      'scheduleInput',
    ]

    this.fields = [
      'occupancyEntryType',
      'occupancyCount',
      'occupancyDensity',
      'activityLevel',
      'scheduleInput',
    ]
    this.serFields = [
      ...this.fields
    ]
    this.childObjs = '$auto'
    this.objInfo = {
      _name: 'People',
      path: 'people',
    }
  }

  getNumOccupants() {
    let res = {};
    if (this.occupancyEntryType.value == OccupancyEntryType.ByPeople) {
      res.result = this.occupancyCount.value;
      let helpText = `You may also enter the occupancy by floor area (aka density). Refer to `
                    + `the reference table below for typical values.`
      res.helpInfo = {
        helpText: helpText
      }
    } else if (this.occupancyEntryType.value == OccupancyEntryType.BySpaceDensity) {
      let floorArea = this.spaceInternals.space.floorArea.value;
      res.result = Math.round(floorArea * this.occupancyDensity.value / 1000.0);
      let helpText = null;
      if (gApp.proj().isImperial()) {
        helpText = `Here, the occupancy is calculated as <b>OccupancyDensity * FloorArea / 1000.0</b>`
      } else {
        helpText = `Here, the occupancy is calculated as <b>OccupancyDensity * FloorArea / 100.0</b>`
      }
      res.helpInfo = {
        helpText: helpText,
        result: {
          label: '# Occupants',
          value: res.result,
          units: Units.None,
        },
        relatedValues: {
          FloorArea: {
            label: 'Floor Area',
            value: floorArea,
            units: Units.ft2,
          }
        }
      }
    } else {
      throw new Error("Unknown occupancy entry type: " + this.occupancyEntryType.value)
    }
    return res;
  }

  getActivityLevel() {
    return this.activityLevel.value;
  }

  getActivityLevelData(ctx) {
    let activityLevelDataTable = gApp.proj().activityLevelsData;
    // TODO - use table row ids instead of labels
    let activityLevel = ActivityLevel._labels[this.activityLevel.value]
    return activityLevelDataTable.getActivityLevelData(activityLevel);
  }

  getSchedule() {
    return this.scheduleInput.getSchedule();
  }

  getTypesSet() {
    let typesSet = TypesSet.create();
    typesSet.addType(this.getSchedule(), 'schedules')
    return typesSet;
  }
}
setupClass(PeopleInternals)

class LightingInternals extends InputComponent {
  init(spaceInternals) {
    this.spaceInternals = spaceInternals;

    this.power = new FieldWithVariableUnits({
      name: 'Power',
      type: FieldType.Load,
      units: Units.WattsPerFt2,
      unitOptions: [
        Units.WattsPerFt2,
        Units.LoadMetric,
        Units.Load,
        Units.LoadPerArea,
      ],
      defaultValue: 0,
    })
    this.powerHelpInfo = {
      helpText: `Enter the power as a total load or a load per area (in imperial or metric units). `
                + `See the reference table below for typical values.\n Also see the "special allowance factors" table.`
    }
    this.powerData = GetLightingPowerTable()
    this.specialAllowanceFactorsData = GetSpecialAllowanceFactorsTable()

    this.radiantFraction = new Field({
      name: 'Radiant Fraction',
      type: FieldType.Percent,
      defaultValue: 50,
    })
    this.spaceFraction = new Field({
      name: 'Space Fraction',
      type: FieldType.Percent,
      defaultValue: 100,
    })
    this.luminaireData = GetLuminaireDataTable()

    this.outputSensibleLoad = new Field({
      name: 'Sensible Load',
      type: FieldType.Load,
      isOutput: true,
    })
    this.outputRadiantFraction = new Field({
      name: 'Radiant Fraction',
      type: FieldType.Percent,
      isOutput: true,
    })
    this.outputSpaceFraction = new Field({
      name: 'Space Fraction',
      type: FieldType.Percent,
      isOutput: true,
    })
    this.outputPlenumFraction = new Field({
      name: 'Plenum Fraction',
      type: FieldType.Percent,
      isOutput: true,
    })
    this.outputsHelpInfo = {
      helpText: `Lighting has a sensible load, a radiant fraction (the percentage of the sensible load that is radiant), and a space fraction `
        + `(the percentage of the sensible load that heats the space. The rest heats the plenum). The luminaire type determines the radiant fraction and space fraction.`
    }
    this.updater.addWatchEffect('outputs', () => {
      let power = this.getPower();
      this.outputSensibleLoad.value = power;
      this.outputRadiantFraction.value = this.radiantFraction.value;
      this.outputSpaceFraction.value = this.spaceFraction.value;
      this.outputPlenumFraction.value = 100.0 - this.outputSpaceFraction.value;
    })

    this.scheduleInput = ScheduleInput.create()

    this.fields = [
      'power',
      'radiantFraction',
      'spaceFraction',
      'scheduleInput',
    ]
    this.serFields = [
      ...this.fields
    ]
    this.childObjs = '$auto'
    this.objInfo = {
      _name: 'Lighting',
      path: 'lighting',
    }
  }

  postReadFromJson(jsonData, opts) {
    if (gApp.proj().getProjectVersion() < '1.3.0') {
      // For compatibility with older projects, if the luminaire type is set,
      // then set the fractions from that. We used to only enter the luminaire type and
      // calculate the fractions from that.
      if ('luminaireType' in jsonData) {
        let luminaireType = jsonData.luminaireType.value;
        console.log(`Compatibility: setting lighting fractions from luminaire type ${luminaireType}`);
        this._setFractionsFromLuminaireType(luminaireType);
      }
    }
  }

  getPower() {
    if (this.power.units == Units.Load) {
      return this.power.value
    } else if (this.power.units == Units.LoadMetric) {
      return this.power.getValueInUnits(Units.Load)
    } else if (this.power.units == Units.LoadPerArea) {
      return this.power.value * this.spaceInternals.space.floorArea.value;
    } else if (this.power.units == Units.WattsPerFt2) {
      return this.power.getValueInUnits(Units.LoadPerArea) * this.spaceInternals.space.floorArea.value;
    } else {
      throw new Error("Unknown power units: " + this.power.units)
    }
  }

  getRadiantAndSpaceFractions() {
    return {
      F_rad: this.radiantFraction.value / 100.0,
      F_space: this.spaceFraction.value / 100.0,
    }
  }

  _setFractionsFromLuminaireType(luminaireType) {
    // Note - this is for compatibility with older files
    // We used to take just the luminaire type instead of entering the fractions directly
    let luminaireDataTable = gApp.proj().luminairesData;
    // The table rowId is the full name of the luminaire type
    let rowId = LuminaireType._labels[luminaireType]
    let luminaireData = luminaireDataTable.getLuminaireData(rowId);

    this.radiantFraction.value = Math.round(luminaireData.F_rad * 100.0);
    this.spaceFraction.value = Math.round(luminaireData.F_space * 100.0);
  }

  getSchedule() {
    return this.scheduleInput.getSchedule();
  }

  getTypesSet() {
    let typesSet = TypesSet.create();
    typesSet.addType(this.getSchedule(), 'schedules')
    return typesSet;
  }
}
setupClass(LightingInternals)

export let ApplianceLoadsEntryType = makeEnum({
  ManualEntry: 'Enter manually',
  SelectAppliance: 'Select from list',
  SelectByOfficeLoadFactor: 'Enter for office',
})

class ApplianceEntry extends InputComponent {
  init(applianceLoads) {
    this.spaceInternals = applianceLoads.spaceInternals;

    this.entryType = Field.makeSelect("Input mode", ApplianceLoadsEntryType, {bold: true})
    this.applianceType = new MultiTieredSelect({
      name: 'Appliance Type',
      type: FieldType.Select,
      optionsMap: ApplianceDataTable.getInstance().getApplianceOptionsMap(),
    })
    this.applianceType.setVisibility(() => {
      return this.entryType.value == ApplianceLoadsEntryType.SelectAppliance
    })
    this.applianceType.helpInfo = null;
    this.applianceType.makeUpdater(() => {
      if (this.entryType.value != ApplianceLoadsEntryType.SelectAppliance) {
        return;
      }
      let data = this.getApplianceData(CalcContext.create());
      this.applianceType.helpInfo = {
        helpText: `Here is the data for this appliance:`,
        relatedValues: {
          'Sensible Load': {
            label: 'Sensible load',
            value: data.S_app,
            units: Units.Load,
          },
          'Radiant Fraction': {
            label: 'Radiant fraction',
            value: data.F_rad * 100.0,
            units: Units.Percent,
          },
          'Convective Fraction': {
            label: 'Convective fraction',
            value: data.F_conv * 100.0,
            units: Units.Percent,
          },
          'Latent Load': {
            label: 'Latent load',
            value: data.L_app,
            units: Units.Load,
          },
        }
      }
    })

    this.computerTypes = Field.makeSelect('Computer types', officeloads.TypicalComputerTypes)
    this.officeLoadsIntensity = new Field({
      name: 'Office Loads Intensity',
      type: FieldType.Select,
      choices: []
    })
    this.officeLoadsIntensity.makeChoicesUpdater(() => {
      let options = lookupData(officeloads.OfficeLoadFactors, [this.computerTypes.value]);
      return makeOptions(officeloads.OfficeLoadIntensities, Object.keys(options))
    })
    this.computerTypes.setVisibility(() => {
      return this.entryType.value == ApplianceLoadsEntryType.SelectByOfficeLoadFactor
    })
    this.officeLoadsIntensity.setVisibility(() => {
      return this.entryType.value == ApplianceLoadsEntryType.SelectByOfficeLoadFactor
    })
    this.officeLoadsIntensity.helpInfo = null;
    this.officeLoadsIntensity.makeUpdater(() => {
      if (this.entryType.value != ApplianceLoadsEntryType.SelectByOfficeLoadFactor) {
        return;
      }
      let data = this.getApplianceData(CalcContext.create());
      this.officeLoadsIntensity.helpInfo = {
        helpText: `Enter the computer types in the office and give an intensity factor. We will estimate the total office appliance loads by `
                  + `<b>SensibleLoadPerArea * FloorArea</b>.`,
        relatedValues: {
          'Sensible Load': {
            label: 'Sensible load / area',
            value: data.intermediateValues.Load,
            units: Units.LoadPerArea,
          },
          'Radiant Fraction': {
            label: 'Radiant fraction',
            value: data.F_rad * 100.0,
            units: Units.Percent,
          },
          'Latent Load': {
            label: 'Latent load / area',
            value: data.intermediateValues.LatentLoad,
            units: Units.LoadPerArea,
          },
          'Floor Area': {
            label: 'Floor area',
            value: this.spaceInternals.space.floorArea.value,
            units: Units.ft2,
          },
        }
      }
    })

    this.manualGroup = FieldGroup.fromDict({
      peakSensiblePowerOutput: new Field({
        name: 'Sensible Power',
        type: FieldType.Load,
        requiresInput: false,
        defaultValue: 0,
      }),
      radiantFraction: new Field({
        name: 'Radiant Fraction',
        type: FieldType.Percent,
        requiresInput: false,
        defaultValue: 40,
      }),
      peakLatentPowerOutput: new Field({
        name: 'Latent Power',
        type: FieldType.Load,
        requiresInput: false,
        defaultValue: 0,
      }),
    })
    this.manualGroup.setVisibility(() => {
      return this.entryType.value == ApplianceLoadsEntryType.ManualEntry
    })

    this.quantity = new Field({
      name: 'Quantity',
      type: FieldType.Count,
      defaultValue: 1,
      allowMin: false,
    })
    this.quantity.makeUpdater((field) => {
      if (this.entryType.value == ApplianceLoadsEntryType.SelectByOfficeLoadFactor) {
        field.visible = false;
        field.value = 1
      } else {
        field.visible = true;
      }
    })

    this.outputSensibleLoad = new Field({
      name: 'Sensible Load',
      type: FieldType.Load,
      isOutput: true,
    })
    this.outputLatentLoad = new Field({
      name: 'Latent Load',
      type: FieldType.Load,
      isOutput: true,
    })
    this.outputFractionRadiant = new Field({
      name: 'Radiant fraction',
      type: FieldType.Percent,
      isOutput: true,
    })
    this.updater.addWatchEffect('appliance-outputs', () => {
      let ctx = CalcContext.create();
      let data = this.getApplianceData(ctx);  
      let N = this.getQuantity();
      this.outputSensibleLoad.value = N * data.S_app;
      this.outputLatentLoad.value = N * data.L_app;
      this.outputFractionRadiant.value = data.F_rad * 100.0;
    })
    this.outputFields = [
      'outputSensibleLoad',
      'outputFractionRadiant',
      'outputLatentLoad',
    ]

    this.scheduleInput = ScheduleInput.create()

    this.serFields = [
      'entryType',
      'applianceType',
      'computerTypes',
      'officeLoadsIntensity',
      'manualGroup',
      'quantity',
      'scheduleInput',
    ]
    this.childObjs = '$auto'
    this.objInfo = {
      _name: 'Appliance',
      path: 'appliance',
    }
  }

  getQuantity() {
    return this.quantity.value
  }

  getSchedule() {
    return this.scheduleInput.getSchedule();
  }

  getTypesSet() {
    let typesSet = TypesSet.create();
    typesSet.addType(this.getSchedule(), 'schedules')
    return typesSet;
  }

  // Use to calculate appliance sensible loads
  getApplianceData(ctx) {
    let res = {}
    res.S_app = 0;
    res.L_app = 0;
    res.F_rad = 0;
    res.F_conv = 0;

    if (this.entryType.value == ApplianceLoadsEntryType.ManualEntry) {
      res.S_app = this.manualGroup.getField('peakSensiblePowerOutput').value
      res.L_app = this.manualGroup.getField('peakLatentPowerOutput').value
      res.F_rad = this.manualGroup.getField('radiantFraction').value / 100.0
      res.F_conv = 1 - res.F_rad
    } else if (this.entryType.value == ApplianceLoadsEntryType.SelectAppliance) {
      res = this._getApplianceDataForSelectApplianceType(ctx)
    } else if (this.entryType.value == ApplianceLoadsEntryType.SelectByOfficeLoadFactor) {
      //console.log("GETTING OFFICE LOAD DATA");
      res = this._getApplianceDataForSelectByOfficeLoadFactorType(ctx)
    } else {
      throw new Error("Unknown appliance entry type: " + this.entryType.value)
    }

    return res
  }

  _getApplianceDataForSelectApplianceType(ctx) {
    if (this.applianceType.value.length === 0) {
      // No appliance selected
      return {
        S_app: 0,
        L_app: 0,
        F_rad: 0,
        F_conv: 0,
      }
    }
    return ApplianceDataTable.getInstance().getApplianceData(
      this.applianceType.value)
  }

  _getApplianceDataForSelectByOfficeLoadFactorType(ctx) {
    let floorArea = this.spaceInternals.space.floorArea.value
    return officeloads.getApplianceDataForOfficeLoads(ctx,
      this.computerTypes.value, this.officeLoadsIntensity.value,
      floorArea);
  }

  getLatentLoad(ctx) {
    return this.getApplianceData(ctx).L_app;
  }
}
setupClass(ApplianceEntry)

class ApplianceLoads {
  init(spaceInternals) {
    this.spaceInternals = spaceInternals;
    this.appliances = new ComponentArray(ApplianceEntry, {creationFunc: () => { return ApplianceEntry.create(this); }})

    this.serFields = [
      ser.arrayField('appliances', () => { return ApplianceEntry.create(this); }),
    ]
    this.childObjs = '$auto'
    this.objInfo = {
      _name: 'Appliances',
      path: 'appliances',
    }
  }

  addAppliance() {
    let appliance = ApplianceEntry.create(this)
    this.appliances.push(appliance)
    return appliance;
  }

  getNumAppliances() {
    return this.appliances.length
  }

  getTypesSet() {
    let typesSet = TypesSet.create();
    for (const appliance of this.appliances) {
      typesSet.addTypes(appliance.getTypesSet());
    }
    return typesSet;
  }
}
setupClass(ApplianceLoads)

class MiscLoads {
  init(spaceInternals) {
    this.spaceInternals = spaceInternals;

    this.sensibleLoads = new FieldWithVariableUnits({
      name: 'Sensible Load',
      type: FieldType.Load,
      units: Units.Load,
      unitOptions: [Units.Load, Units.LoadPerArea, Units.WattsPerFt2],
    })
    this.sensibleLoadsRadiantFraction = new Field({
      name: 'Radiant Fraction',
      type: FieldType.Percent,
      defaultValue: 50,
    })
    this.sensibleLoadsFields = [
      'sensibleLoads',
      'sensibleLoadsRadiantFraction',
    ]

    this.latentLoads = new FieldWithVariableUnits({
      name: 'Latent Load',
      type: FieldType.Load,
      unitOptions: [Units.Load, Units.LoadPerArea, Units.WattsPerFt2],
    })
    this.latentLoadsFields = [
      'latentLoads',
    ]

    this.scheduleInput = ScheduleInput.create({enableDiversityFactor: false})

    this.serFields = [
      'sensibleLoads',
      'sensibleLoadsRadiantFraction',
      'latentLoads',
      'scheduleInput',
    ]
    this.childObjs = '$auto'
    this.objInfo = {
      _name: 'Misc Loads',
      path: 'misc',
    }
  }

  getSchedule() {
    return this.scheduleInput.getSchedule();
  }

  getSensibleLoad() {
    if (this.sensibleLoads.units == Units.Load) {
      return this.sensibleLoads.value
    } else if (this.sensibleLoads.units == Units.LoadPerArea) {
      return this.sensibleLoads.value * this.spaceInternals.space.floorArea.value
    } else if (this.sensibleLoads.units == Units.WattsPerFt2) {
      return this.sensibleLoads.getValueInUnits(Units.LoadPerArea) * this.spaceInternals.space.floorArea.value
    } else {
      throw new Error("Unknown sensible load units: " + this.sensibleLoads.units)
    }
  }

  getSensibleLoadRadiantFraction() {
    return this.sensibleLoadsRadiantFraction.value / 100.0;
  }

  getLatentLoad(ctx) {
    if (this.latentLoads.units == Units.Load) {
      return this.latentLoads.value
    } else if (this.latentLoads.units == Units.LoadPerArea) {
      return this.latentLoads.value * this.spaceInternals.space.floorArea.value
    } else if (this.latentLoads.units == Units.WattsPerFt2) {
      return this.latentLoads.getValueInUnits(Units.LoadPerArea) * this.spaceInternals.space.floorArea.value
    } else {
      throw new Error("Unknown latent load units: " + this.latentLoads.units)
    }
  }

  getTypesSet() {
    let typesSet = TypesSet.create();
    typesSet.addType(this.getSchedule(), 'schedules')
    return typesSet;
  }
}
setupClass(MiscLoads)

export class SpaceInternals {
  init(space) {
    this.space = space;

    this.people = PeopleInternals.create(this)
    this.lighting = LightingInternals.create(this)
    this.appliances = ApplianceLoads.create(this)
    this.miscLoads = MiscLoads.create(this)

    this.serFields = [
      'people',
      'lighting',
      'appliances',
      'miscLoads',
    ]
    this.childObjs = '$auto'
    this.objInfo = {
      _name: 'Internals',
      path: 'internals',
    }
  }

  getNumAppliances() {
    return this.appliances.getNumAppliances()
  }

  getLightingPower() {
    return this.lighting.getPower();
  }

  getMiscLoad() {
    return this.miscLoads.getSensibleLoad();
  }

  getTypesSet() {
    let typesSet = TypesSet.create();
    typesSet.addTypes(this.people.getTypesSet())
    typesSet.addTypes(this.lighting.getTypesSet())
    typesSet.addTypes(this.appliances.getTypesSet())
    typesSet.addTypes(this.miscLoads.getTypesSet())
    return typesSet;
  }
}
setupClass(SpaceInternals)
